import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { RegisterStudentModal } from './Students';
import Swal from 'sweetalert2';

function StudentsWithoutRegistration() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  });
  const [activeStudent, setActiveStudent] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const fetchStudents = async (customSearch = null, pageOverride = null) => {
    try {
      setLoading(true);
      const page = pageOverride ?? pagination.current_page;
      const params = {
        page,
        perPage: pagination.per_page,
      };

      const searchValue = customSearch !== null ? customSearch : searchTerm;
      if (searchValue) {
        params.search = searchValue;
      }

      const resp = await axiosInstance.get('/admin/students/without-registration', { params });
      const data = resp.data || {};

      const apiPagination = data.pagination || {};
      const total = Number(data.total ?? apiPagination.total ?? 0) || (data.students?.length || 0);
      const perPage = Number(apiPagination.per_page ?? pagination.per_page ?? 50) || 50;
      const calculatedLastPage = Math.max(1, Math.ceil(total / perPage));
      const currentPage = Math.min(
        Math.max(1, Number(apiPagination.current_page ?? page ?? 1)),
        calculatedLastPage
      );

      setStudents(data.students || []);
      setPagination({
        current_page: currentPage,
        last_page: Number(apiPagination.last_page ?? calculatedLastPage) || calculatedLastPage,
        per_page: perPage,
        total,
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching students without registration:', err);
      setError('Failed to load students without registration. Please try again.');
      setStudents([]);
      setPagination((prev) => ({ ...prev, total: 0, current_page: 1, last_page: 1 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const term = searchInput.trim();
    setSearchTerm(term);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchStudents(term, 1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchStudents('', 1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const gotoPage = (page) => {
    if (page < 1 || page > pagination.last_page || page === pagination.current_page) return;
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchStudents(null, page);
  };

  const handleDelete = async (student) => {
    const result = await Swal.fire({
      title: 'Delete student?',
      html: `<p>You are about to delete <strong>${student.fullName || ''}</strong> (${student.studentId}).</p>
             <p class="mt-2 text-sm text-slate-600">This action cannot be undone.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      focusCancel: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/students/${student.studentId}`);
      await Swal.fire({
        title: 'Deleted!',
        text: 'Student has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#64748b',
      });
      fetchStudents();
    } catch (err) {
      await Swal.fire({
        title: 'Error',
        text: err.response?.data?.message || 'Failed to delete student.',
        icon: 'error',
        confirmButtonColor: '#64748b',
      });
    }
  };

  const handleRegister = (student) => {
    setActiveStudent(student);
    setShowRegisterModal(true);
  };

  return (
    <DashboardLayout title="Students Without Registration">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Students Without Registration</h2>
          <p className="mt-1 text-sm text-gray-600">
            Showing students whose IDs are not present in the registrations table.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex flex-1 min-w-[280px] gap-2">
          <input
            type="text"
            placeholder="Search by name, student ID, email, NIC, mobile..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={handleSearch}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Find
          </button>
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="flex items-center gap-2 rounded-lg bg-gray-500 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600"
            >
              Clear
            </button>
          )}
        </div>
        <div className="text-sm text-gray-600">
          Total: <span className="font-semibold">{pagination.total}</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NIC</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                  Loading students...
                </td>
              </tr>
            ) : students.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                  No students found without registration.
                </td>
              </tr>
            ) : (
              students.map((s) => (
                <tr key={s.studentId}>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{s.studentId}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{s.fullName}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{s.email || '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{s.telephone || '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{s.nicNumber || '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleRegister(s)}
                        className="rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                      >
                        Register
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(s)}
                        className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.last_page > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <div>
            Page {pagination.current_page} of {pagination.last_page}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => gotoPage(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
              className="rounded-md border px-3 py-1 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => gotoPage(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.last_page}
              className="rounded-md border px-3 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showRegisterModal && activeStudent && (
        <RegisterStudentModal
          student={activeStudent}
          onClose={() => {
            setShowRegisterModal(false);
            setActiveStudent(null);
          }}
          onSuccess={() => {
            setShowRegisterModal(false);
            setActiveStudent(null);
            fetchStudents();
          }}
        />
      )}
    </DashboardLayout>
  );
}

export default StudentsWithoutRegistration;
